import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    formatDate,
    getScheduleEntryForDate,
} from "./ReservationDateTimeSelectorUtils";

const weekDaysShort = ["ma", "di", "wo", "do", "vr", "za", "zo"];
const monthNamesShort = [
    "jan",
    "feb",
    "mrt",
    "apr",
    "mei",
    "jun",
    "jul",
    "aug",
    "sep",
    "okt",
    "nov",
    "dec",
];

const stripTime = (date) => (
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)
);

const addMonths = (date, months) => (
    new Date(date.getFullYear(), date.getMonth() + months, 1)
);

const isSameDay = (left, right) => (
    left &&
    right &&
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
);

export default function ReservationSingleDayCalendarModal({
    isOpen = false,
    selectedDate = new Date(),
    minDate = null,
    maxDate = null,
    locationSchedule = [],
    disableClosedDays = false,
    onRequestClose,
    onConfirm,
}) {
    const selectedDay = useMemo(() => stripTime(selectedDate), [selectedDate]);
    const [monthCursor, setMonthCursor] = useState(
        () => new Date(selectedDay.getFullYear(), selectedDay.getMonth(), 1)
    );
    const minDay = useMemo(() => (minDate ? stripTime(minDate) : null), [minDate]);
    const maxDay = useMemo(() => (maxDate ? stripTime(maxDate) : null), [maxDate]);
    const calendarRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return;
        const nextSelectedDay = stripTime(selectedDate);
        setMonthCursor(new Date(nextSelectedDay.getFullYear(), nextSelectedDay.getMonth(), 1));
    }, [isOpen, selectedDate]);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDownOutside = (event) => {
            if (!calendarRef.current) return;
            if (calendarRef.current.contains(event.target)) return;
            if (typeof onRequestClose === "function") {
                onRequestClose();
            }
        };

        document.addEventListener("mousedown", handlePointerDownOutside);
        return () => document.removeEventListener("mousedown", handlePointerDownOutside);
    }, [isOpen, onRequestClose]);

    const isDateDisabled = (date) => {
        if (minDay && date.getTime() < minDay.getTime()) return true;
        if (maxDay && date.getTime() > maxDay.getTime()) return true;
        if (!disableClosedDays) return false;

        const scheduleEntry = getScheduleEntryForDate(date, locationSchedule);
        return Boolean(scheduleEntry?.isClosed);
    };

    const renderMonth = (monthDate) => {
        const year = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const firstDay = new Date(year, month, 1);
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const leadingEmpty = (firstDay.getDay() + 6) % 7;
        const cells = [];

        for (let index = 0; index < leadingEmpty; index += 1) {
            cells.push(null);
        }

        for (let day = 1; day <= daysInMonth; day += 1) {
            cells.push(new Date(year, month, day, 0, 0, 0, 0));
        }

        return (
            <div className="kav-rdt2-cal__month" key={`${year}-${month}`}>
                <div className="kav-rdt2-cal__weekdays">
                    {weekDaysShort.map((label) => (
                        <span key={`${year}-${month}-${label}`}>{label}</span>
                    ))}
                </div>

                <div className="kav-rdt2-cal__grid">
                    {cells.map((cell, index) => {
                        if (!cell) {
                            return (
                                <span
                                    key={`${year}-${month}-empty-${index}`}
                                    className="kav-rdt2-cal__empty"
                                    aria-hidden="true"
                                />
                            );
                        }

                        const normalized = stripTime(cell);
                        const isDisabled = isDateDisabled(normalized);
                        const isSelected = isSameDay(normalized, selectedDay);
                        const isToday = isSameDay(normalized, stripTime(new Date()));
                        const dayEntry = getScheduleEntryForDate(normalized, locationSchedule);
                        const isClosedDay = Boolean(dayEntry?.isClosed);
                        const dayClass = [
                            "kav-rdt2-cal__day",
                            isSelected ? "is-selected" : "",
                            isToday ? "is-today" : "",
                            isClosedDay ? "is-closed" : "",
                            isDisabled ? "is-disabled" : "",
                        ]
                            .filter(Boolean)
                            .join(" ");

                        return (
                            <button
                                key={`${year}-${month}-${normalized.getDate()}`}
                                type="button"
                                className={dayClass}
                                onClick={() => {
                                    if (isDisabled) return;
                                    if (typeof onConfirm === "function") {
                                        onConfirm(normalized);
                                    }
                                }}
                                disabled={isDisabled}
                                aria-pressed={isSelected}
                                aria-label={formatDate(normalized)}
                            >
                                {normalized.getDate()}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="kav-rdt2-calInline" role="dialog" aria-label="Pick-up date calendar">
            <div className="kav-rdt2-calInline__card" ref={calendarRef}>
                <div className="kav-rdt2-cal__nav">
                    <button
                        type="button"
                        className="kav-rdt2-cal__navBtn"
                        onClick={() => setMonthCursor((prev) => addMonths(prev, -1))}
                        aria-label="Previous month"
                    >
                        <i className="bi bi-chevron-left" aria-hidden="true"></i>
                    </button>

                    <div className="kav-rdt2-cal__monthLabel">
                        {monthNamesShort[monthCursor.getMonth()]} {monthCursor.getFullYear()}
                    </div>

                    <button
                        type="button"
                        className="kav-rdt2-cal__navBtn"
                        onClick={() => setMonthCursor((prev) => addMonths(prev, 1))}
                        aria-label="Next month"
                    >
                        <i className="bi bi-chevron-right" aria-hidden="true"></i>
                    </button>
                </div>

                <div className="kav-rdt2-cal__months">
                    {renderMonth(monthCursor)}
                </div>
            </div>
        </div>
    );
}
